import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { ArrowLeft, CheckCircle, Clock } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import ListingForm from '../components/ListingForm';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateListingPage = () => {
  const { t } = useTranslation();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!user) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 text-center max-w-md">
          <h2 className="text-2xl font-bold text-white mb-4">{t('common.loginRequired')}</h2>
          <p className="text-zinc-400 mb-6">
            {t('common.pleaseLoginToCreate')}
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {t('common.backToHome')}
          </Button>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-zinc-800 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 text-center max-w-md">
          <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Clock className="w-8 h-8 text-amber-500" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-4">{t('common.listingCreatedSuccess')}</h2>
          <p className="text-zinc-400 mb-6">
            {t('common.listingCreatedDesc')}
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/my-listings')}
              className="w-full bg-emerald-600 hover:bg-emerald-700"
            >
              {t('common.goToMyListings')}
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            >
              {t('common.backToHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (formData) => {
    setSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/listings`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error creating listing:', err);
      setError(err.response?.data?.detail || t('common.listingCreateError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-800">
      {/* Breadcrumb */}
      <Breadcrumb items={[{ label: t('common.createNewListing'), href: null }]} />

      {/* Hero Section */}
      <div className="border-b border-zinc-800 py-12 bg-gradient-to-b from-zinc-900 to-black">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">{t('common.createNewListing')}</h1>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            {t('common.publishListing')}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">

        <div className="max-w-2xl mx-auto">
          <div className="bg-zinc-900 rounded-2xl p-6 md:p-8 border border-zinc-800">

            <ListingForm
              initialData={{ name: user?.name || '' }}
              onSubmit={handleSubmit}
              submitting={submitting}
              error={error}
              submitButtonText={t('common.publishListing')}
              loadingButtonText={t('common.publishing')}
              token={token}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateListingPage;
